<template>
  <div class="media-library-management">
    <div class="header">
      <div class="header-content">
        <div class="left-section">
          <el-button 
            type="text" 
            icon="el-icon-arrow-left" 
            @click="goBack"
            class="back-btn">
            返回
          </el-button>
          <div class="title-container">
            <i class="el-icon-folder-opened"></i>
            <span>媒体库管理</span>
          </div>
        </div>
        <div class="user-info">
          <div class="avatar-container">
            <img :src="getUserAvatar()" alt="" class="user-avatar">
            <div class="online-indicator"></div>
          </div>
          <div class="user-details">
            <span class="name">{{ getUserName() || 'XXX' }}</span>
            <span class="status">在线</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 媒体类型切换 -->
    <div class="media-tabs">
      <div class="tabs-container">
        <el-tabs v-model="activeMediaType" class="management-tabs">
          <el-tab-pane name="photo">
            <span slot="label">
              <i class="el-icon-camera-solid"></i>
              <span>照片</span>
            </span>
          </el-tab-pane>
          <el-tab-pane name="video">
            <span slot="label">
              <i class="el-icon-video-camera-solid"></i>
              <span>视频</span>
            </span>
          </el-tab-pane>
          <el-tab-pane name="audio">
            <span slot="label">
              <i class="el-icon-message-solid"></i>
              <span>音频</span>
            </span>
          </el-tab-pane>
        </el-tabs>
        <div class="delete-btn-container">
          <div class="commonDiv delete-btn" :class="{ active: selectedItems.length > 0 }" @click="deleteSelectedItems">
            <i class="el-icon-delete"></i>
            <span>删除({{ selectedItems.length }})</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 媒体内容区域 -->
    <div class="media-content">
      <!-- 照片管理 -->
      <PhotoLibrary 
        v-if="activeMediaType === 'photo'" 
        ref="photoLibrary" 
        :chat-user-info="chatUserInfo"
        @item-selected="handleItemSelected"
        @item-deselected="handleItemDeselected" 
      />

      <!-- 视频管理 -->
      <VideoLibrary 
        v-if="activeMediaType === 'video'" 
        ref="videoLibrary" 
        :chat-user-info="chatUserInfo"
        @item-selected="handleItemSelected"
        @item-deselected="handleItemDeselected" 
      />

      <!-- 音频管理 -->
      <AudioLibrary
        v-if="activeMediaType === 'audio'"
        ref="audioLibrary" 
        :chat-user-info="chatUserInfo"
        @item-selected="handleItemSelected"
        @item-deselected="handleItemDeselected" 
      />
    </div>

  </div>
</template>

<script>
import ManagementPhotoLibrary from './components/ManagementPhotoLibrary.vue'
import ManagementVideoLibrary from './components/ManagementVideoLibrary.vue'
import ManagementAudioLibrary from './components/ManagementAudioLibrary.vue'
import { getTalkytimesUserInfo } from '@/api/talkytimes/user'

export default {
  name: 'MediaLibraryManagement',
  components: {
    PhotoLibrary: ManagementPhotoLibrary,
    VideoLibrary: ManagementVideoLibrary,
    AudioLibrary: ManagementAudioLibrary
  },
  data() {
    return {
      activeMediaType: 'photo',
      selectedItems: [],
      chatUserInfo: {}
    }
  },
  mounted() {
    this.initUserInfo()
    // 根据路由参数设置默认媒体类型
    if (this.$route.query.mediaType) {
      this.activeMediaType = this.$route.query.mediaType
    }
    // 等待组件渲染后设置状态过滤为 'all'，以显示所有数据
    this.$nextTick(() => {
      this.setAllMediaFiltersToAll()
    })
  },
  // 如果使用了 keep-alive，使用 activated 钩子确保每次激活时刷新
  activated() {
    this.loadUserInfo()
    this.forceRefreshAllMedia()
  },
  // 路由进入前刷新数据
  beforeRouteEnter(to, from, next) {
    next(vm => {
      // 在组件实例创建后，强制刷新数据
      vm.$nextTick(() => {
        vm.loadUserInfo()
        vm.forceRefreshAllMedia()
      })
    })
  },
  // 路由更新时刷新数据
  beforeRouteUpdate(to, from, next) {
    this.loadUserInfo()
    this.forceRefreshAllMedia()
    next()
  },
  watch: {
    activeMediaType() {
      // 切换媒体类型时，也要设置过滤为 'all' 并刷新
      this.$nextTick(() => {
        this.setAllMediaFiltersToAll()
      })
    }
  },
  methods: {
    initUserInfo() {
      // 从 localStorage 获取基本用户信息（用于初始化）
      const activeUserStr = localStorage.getItem('activeUser')
      if (activeUserStr) {
        const activeUser = JSON.parse(activeUserStr)
        // 在管理模式下，userId 和 idInterlocutor 都应该是自己的平台用户ID
        this.chatUserInfo = {
          platformUserId: activeUser.id || activeUser.platformUserId,
          userId: activeUser.id || activeUser.platformUserId || activeUser.userId,
          idInterlocutor: activeUser.id || activeUser.platformUserId || activeUser.userId,
          platformUserDetail: activeUser,
          userDetail: {}
        }
      }
      // 立即加载最新的用户信息
      this.loadUserInfo()
    },
    async loadUserInfo() {
      // 每次进入页面时重新获取最新的用户信息
      const activeUserStr = localStorage.getItem('activeUser')
      if (!activeUserStr) {
        return
      }
      
      const activeUser = JSON.parse(activeUserStr)
      const platformUserId = activeUser.id || activeUser.platformUserId
      
      if (!platformUserId) {
        return
      }

      getTalkytimesUserInfo().then(res => {
        if (res && res.id) {
          // 更新用户信息
          // 在管理模式下，userId 和 idInterlocutor 都应该是自己的平台用户ID
          this.chatUserInfo = {
            platformUserId: res.id,
            userId: res.id || res.userId || activeUser.userId || activeUser.id,
            idInterlocutor: res.id || res.userId || activeUser.userId || activeUser.id,
            platformUserDetail: {
              ...res,
              name: res.name || res.username,
              personalAvatar_xl: res.avatar_large || res.personalAvatar_xl,
              avatar_large: res.avatar_large || res.personalAvatar_xl,
              avatar_small: res.avatar_small || res.thumbnailUrl,
              thumbnailUrl: res.thumbnailUrl || res.avatar_small
            },
            userDetail: {}
          }
        }
      }).catch(error => {
        console.error('获取用户信息失败:', error)
        // 失败时使用 localStorage 中的信息
        const activeUserStr = localStorage.getItem('activeUser')
        if (activeUserStr) {
          const activeUser = JSON.parse(activeUserStr)
          this.chatUserInfo = {
            platformUserId: activeUser.id || activeUser.platformUserId,
            userId: activeUser.id || activeUser.platformUserId || activeUser.userId,
            idInterlocutor: activeUser.id || activeUser.platformUserId || activeUser.userId,
            platformUserDetail: activeUser,
            userDetail: {}
          }
        }
      })
    },
    getUserName() {
      if (this.chatUserInfo.platformUserDetail && this.chatUserInfo.platformUserDetail.name) {
        return this.chatUserInfo.platformUserDetail.name
      }
      const activeUserStr = localStorage.getItem('activeUser')
      if (activeUserStr) {
        const activeUser = JSON.parse(activeUserStr)
        return activeUser.name || 'XXX'
      }
      return 'XXX'
    },
    getUserAvatar() {
      if (this.chatUserInfo.platformUserDetail && this.chatUserInfo.platformUserDetail.personalAvatar_xl) {
        return this.chatUserInfo.platformUserDetail.personalAvatar_xl
      }
      const activeUserStr = localStorage.getItem('activeUser')
      if (activeUserStr) {
        const activeUser = JSON.parse(activeUserStr)
        return activeUser.personalAvatar_xl || require('@/assets/profile.png')
      }
      return require('@/assets/profile.png')
    },
    handleItemSelected(item) {
      if (!this.selectedItems.find(i => i.id === item.id)) {
        this.selectedItems.push(item)
      }
    },
    handleItemDeselected(item) {
      const index = this.selectedItems.findIndex(i => i.id === item.id)
      if (index > -1) {
        this.selectedItems.splice(index, 1)
      }
    },
    async deleteSelectedItems() {
      if (this.selectedItems.length === 0) {
        this.$message.warning('请选择要删除的项目')
        return
      }

      this.$confirm(`确定要删除选中的 ${this.selectedItems.length} 个项目吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        const currentLibrary = this.$refs[`${this.activeMediaType}Library`]
        if (currentLibrary && currentLibrary.deleteItems) {
          // 保存要删除的项目ID，用于后续清理选中列表
          const itemIds = this.selectedItems.map(item => item.id || item.idPhoto || item.idVideo || item.idAudio)
          
          // 调用删除方法
          await currentLibrary.deleteItems(this.selectedItems)
          
          // 从选中列表中移除已删除的项目
          this.selectedItems = this.selectedItems.filter(item => {
            const itemId = item.id || item.idPhoto || item.idVideo || item.idAudio
            return !itemIds.includes(itemId)
          })
          
          // 删除成功后，刷新数据以获取最新列表
          if (currentLibrary.refresh) {
            currentLibrary.refresh()
          }
        } else {
          this.$message.warning('当前媒体类型不支持删除操作')
        }
      }).catch(() => {
        // 取消删除
      })
    },
    goBack() {
      // 返回上一页或消息页面
      if (this.$route.query.from) {
        this.$router.go(-1)
      } else {
        this.$router.push('/talkytimes/message')
      }
    },
    setAllMediaFiltersToAll() {
      // 设置所有媒体库组件的状态过滤为 'all'，以显示所有数据（不过滤）
      // 新的管理组件会在 statusFilter 改变时自动刷新
      if (this.$refs.photoLibrary) {
        this.$refs.photoLibrary.statusFilter = 'all'
      }
      if (this.$refs.videoLibrary) {
        this.$refs.videoLibrary.statusFilter = 'all'
      }
      if (this.$refs.audioLibrary) {
        this.$refs.audioLibrary.statusFilter = 'all'
      }
    },
    forceRefreshAllMedia() {
      // 强制刷新所有媒体库组件的数据，清除缓存
      if (this.$refs.photoLibrary) {
        this.$refs.photoLibrary.statusFilter = 'all'
        if (this.$refs.photoLibrary.refresh) {
          this.$refs.photoLibrary.refresh()
        }
      }
      if (this.$refs.videoLibrary) {
        this.$refs.videoLibrary.statusFilter = 'all'
        if (this.$refs.videoLibrary.refresh) {
          this.$refs.videoLibrary.refresh()
        }
      }
      if (this.$refs.audioLibrary) {
        this.$refs.audioLibrary.statusFilter = 'all'
        if (this.$refs.audioLibrary.refresh) {
          this.$refs.audioLibrary.refresh()
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.media-library-management {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f5f5f5;

  .header {
    background: #fff;
    border-bottom: 1px solid #e4e7ed;
    padding: 20px 30px;

      .header-content {
        display: flex;
        justify-content: space-between;
        align-items: center;

        .left-section {
          display: flex;
          align-items: center;
          gap: 20px;

          .back-btn {
            padding: 0;
            font-size: 14px;
            color: #606266;

            &:hover {
              color: #409eff;
            }
          }

          .title-container {
            display: flex;
            align-items: center;
            gap: 10px;
            font-size: 20px;
            font-weight: 600;
            color: #303133;

            i {
              font-size: 24px;
              color: #409eff;
            }
          }
        }

      .user-info {
        display: flex;
        align-items: center;
        gap: 12px;

        .avatar-container {
          position: relative;

          .user-avatar {
            width: 40px;
            height: 40px;
            border-radius: 50%;
            object-fit: cover;
          }

          .online-indicator {
            position: absolute;
            bottom: 0;
            right: 0;
            width: 12px;
            height: 12px;
            background: #67c23a;
            border: 2px solid #fff;
            border-radius: 50%;
          }
        }

        .user-details {
          display: flex;
          flex-direction: column;

          .name {
            font-size: 14px;
            font-weight: 500;
            color: #303133;
          }

          .status {
            font-size: 12px;
            color: #909399;
          }
        }
      }
    }
  }

  .media-tabs {
    background: #fff;
    border-bottom: 1px solid #e4e7ed;
    padding: 0 30px;

    .tabs-container {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    ::v-deep .management-tabs {
      flex: 1;
      
      .el-tabs__header {
        margin: 0;
      }

      .el-tabs__item {
        padding: 0 20px;
        height: 50px;
        line-height: 50px;
        font-size: 14px;

        i {
          margin-right: 5px;
        }
      }
    }

    .delete-btn-container {
      display: flex;
      align-items: center;
      margin-left: 20px;

      .commonDiv {
        display: flex;
        align-items: center;
        gap: 5px;
        padding: 8px 16px;
        border-radius: 4px;
        cursor: pointer;
        font-size: 14px;
        transition: all 0.3s;

        &.delete-btn {
          background: #f56c6c;
          color: #fff;

          &:hover {
            background: #f78989;
          }

          &:not(.active) {
            opacity: 0.5;
            cursor: not-allowed;
          }
        }
      }
    }
  }

  .media-content {
    flex: 1;
    overflow: auto;
    padding: 20px 30px;

    // 在管理页面中隐藏所有蒙版
    ::v-deep .disabled-overlay {
      display: none !important;
    }

    // 在管理页面中移除禁用样式
    ::v-deep .disabled-image {
      opacity: 1 !important;
      filter: none !important;
    }
  }

}
</style>

